import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Boat, ClockCountdown, ArrowLeft } from "@phosphor-icons/react";
import { BRAND, PortalLogo } from "./portalBrand";

const POINTS = [
  { icon: Boat, text: "Track every shipment in real time" },
  { icon: ClockCountdown, text: "Live ETAs and voyage progress" },
  { icon: ShieldCheck, text: "Your documents, secure and in one place" },
];

/**
 * Shared layout for the portal's unauthenticated pages (forgot password, reset
 * password, request access) so they match PortalLogin exactly — same brand panel,
 * same card. Brand name/logo come from useBrand() via PortalLogo, so each domain
 * shows its own identity.
 */
export default function PortalAuthShell({ title, subtitle, children, backTo = "/portal/login", backLabel = "Back to sign in" }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-50">
      <div className="hidden lg:flex flex-col justify-between p-12 text-white relative overflow-hidden"
        style={{ background: "linear-gradient(135deg,#1e3a8a 0%,#1d4ed8 55%,#2563eb 100%)" }}>
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #fff 0, transparent 40%), radial-gradient(circle at 80% 70%, #fff 0, transparent 35%)" }} />
        <div className="relative">
          <div className="bg-white rounded-lg inline-flex px-4 py-3 shadow-lg"><PortalLogo className="h-10 w-auto object-contain" /></div>
          <h1 className="mt-10 text-3xl font-bold leading-tight">Your shipments,<br />tracked end to end.</h1>
          <p className="mt-3 text-blue-100 max-w-sm">{BRAND.tagline}</p>
          <ul className="mt-8 space-y-3">
            {POINTS.map((p, i) => (
              <li key={i} className="flex items-center gap-3 text-blue-50">
                <span className="w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center"><p.icon size={18} weight="bold" /></span>
                {p.text}
              </li>
            ))}
          </ul>
        </div>
        <div className="relative text-[12px] text-blue-200">
          Need help? <a href={`mailto:${BRAND.supportEmail}`} className="underline hover:text-white">{BRAND.supportEmail}</a>
        </div>
      </div>

      <div className="flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex justify-center mb-6"><PortalLogo className="h-10 w-auto object-contain" fallbackClass="text-xl" /></div>
          <h2 className="text-2xl font-bold text-slate-900">{title}</h2>
          {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
          {children}
          {backTo && (
            <Link to={backTo} className="mt-5 inline-flex items-center gap-1.5 text-[13px] text-slate-500 hover:text-blue-700">
              <ArrowLeft size={13} /> {backLabel}
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

export const authCard = "mt-6 bg-white border border-slate-200 rounded-xl shadow-sm p-6 space-y-4";
export const authLabel = "text-[11px] font-semibold uppercase tracking-wider text-slate-500";
export const authInput = "mt-1.5 w-full h-11 px-3 bg-slate-50 border border-slate-300 rounded-md text-sm text-slate-900 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100";
export const authButton = "w-full h-11 rounded-md bg-blue-700 hover:bg-blue-800 text-white text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50 transition-colors";
